import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSocket } from '../contexts/SocketContext';

interface Board {
  id: string;
  name: string;
  type: string;
  description: string;
  capacity: number;
  slug?: string;
  availabilitySettings: any;
  pricing: any;
  amenities?: string[];
  policies?: any;
  rules?: string;
  cancellationPolicy?: string;
  isActive?: boolean;
  businessId?: string; // Added to avoid extra API call
  business?: { name?: string; slug?: string }; // Added for sharing links
}

interface Reservation {
  id: string;
  clientId?: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  startDate: string;
  endDate: string;
  status: string;
  boardId?: string | number;
  statusHistory?: any[];
  paymentStatus?: 'pending' | 'partial' | 'paid' | 'refunded';
  completionStatus?: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'no_show' | 'cancelled';
  rescheduleCount?: number;
  lastRescheduleDate?: string;
  notes?: string;
  isDeleted?: boolean; // Added to distinguish deleted reservations
}

export const useBoardData = (boardId: string | undefined) => {
  const [board, setBoard] = useState<Board | null>(null);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [deletedReservations, setDeletedReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const socket = useSocket();

  // Optimized: Fetch all data in parallel
  const fetchAllData = useCallback(async () => {
    if (!boardId) return;

    try {
      setLoading(true);

      // Execute all API calls in parallel for faster loading
      const [boardResponse, reservationsResponse] = await Promise.all([
        axios.get(`/api/boards/${boardId}`),
        axios.get(`/api/boards/${boardId}/reservations`)
      ]);

      // Process board data
      const boardData = boardResponse.data;
      const formattedBoard: Board = {
        id: boardData.id,
        name: boardData.name,
        type: boardData.type,
        description: boardData.description || '',
        capacity: boardData.capacity,
        slug: boardData.slug || '',
        availabilitySettings: boardData.availabilitySettings || {},
        pricing: boardData.pricing || {},
        amenities: boardData.amenities || [],
        rules: boardData.rules || '',
        cancellationPolicy: boardData.cancellationPolicy || '',
        policies: boardData.policies || {},
        isActive: boardData.isActive,
        businessId: boardData.businessId, // Store businessId to avoid extra calls
        business: boardData.business
      };

      setBoard(formattedBoard);

      // Process reservations - separate active and deleted
      const allReservations = reservationsResponse.data;
      const active = allReservations.filter((r: Reservation) => !r.isDeleted);
      const deleted = allReservations.filter((r: Reservation) => r.isDeleted);

      setReservations(active);
      setDeletedReservations(deleted);
      setError('');
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  const fetchBoard = useCallback(async () => {
    if (!boardId) return;

    try {
      const response = await axios.get(`/api/boards/${boardId}`);
      const boardData = response.data;

      const formattedBoard: Board = {
        id: boardData.id,
        name: boardData.name,
        type: boardData.type,
        description: boardData.description || '',
        capacity: boardData.capacity,
        slug: boardData.slug || '',
        availabilitySettings: boardData.availabilitySettings || {},
        pricing: boardData.pricing || {},
        amenities: boardData.amenities || [],
        rules: boardData.rules || '',
        cancellationPolicy: boardData.cancellationPolicy || '',
        policies: boardData.policies || {},
        isActive: boardData.isActive,
        businessId: boardData.businessId,
        business: boardData.business
      };

      setBoard(formattedBoard);
      setError('');
    } catch (err: any) {
      console.error('Error fetching board:', err);
      setError('Error al cargar el tablero');
    }
  }, [boardId]);

  const fetchReservations = useCallback(async () => {
    if (!boardId) return;

    try {
      const response = await axios.get(`/api/boards/${boardId}/reservations`);
      const allReservations = response.data;
      const active = allReservations.filter((r: Reservation) => !r.isDeleted);
      const deleted = allReservations.filter((r: Reservation) => r.isDeleted);

      setReservations(active);
      setDeletedReservations(deleted);
    } catch (err: any) {
      console.error('Error fetching reservations:', err);
      setError(`Error al cargar las reservas: ${err.response?.data?.message || err.message}`);
    }
  }, [boardId]);

  const updateBoard = useCallback(async (boardData: Partial<Board>) => {
    if (!boardId || !board) return;

    try {
      // Use cached businessId instead of making another API call
      const businessId = board.businessId;

      if (!businessId) {
        // Fallback: fetch if not available
        const boardResponse = await axios.get(`/api/boards/${boardId}`);
        await axios.put(`/api/businesses/${boardResponse.data.businessId}/boards/${boardId}`, boardData);
      } else {
        await axios.put(`/api/businesses/${businessId}/boards/${boardId}`, boardData);
      }

      await fetchBoard(); // Refresh board data
      return true;
    } catch (err: any) {
      console.error('Error updating board:', err);
      setError('Error al actualizar el tablero');
      return false;
    }
  }, [boardId, board, fetchBoard]);

  const updateReservation = useCallback(async (reservationId: string, reservationData: Partial<Reservation>) => {
    try {
      await axios.put(`/api/reservations/${reservationId}`, reservationData);
      await fetchReservations(); // Refresh reservations
      return true;
    } catch (err: any) {
      setError('Error al actualizar la reserva');
      return false;
    }
  }, [fetchReservations]);

  const deleteReservation = useCallback(async (reservationId: string) => {
    try {
      await axios.delete(`/api/reservations/${reservationId}`);
      await fetchReservations(); // This now fetches both active and deleted
      return true;
    } catch (err: any) {
      setError('Error al eliminar la reserva');
      return false;
    }
  }, [fetchReservations]);

  const restoreReservation = useCallback(async (reservationId: string) => {
    try {
      await axios.post(`/api/reservations/${reservationId}/restore`);
      await fetchReservations(); // This now fetches both active and deleted
      return true;
    } catch (err: any) {
      setError('Error al restaurar la reserva');
      return false;
    }
  }, [fetchReservations]);

  useEffect(() => {
    if (boardId) {
      fetchAllData();
    }
  }, [boardId, fetchAllData]);

  // Real-time synchronization
  useEffect(() => {
    if (!socket || !boardId) return;

    socket.emit('joinBoard', boardId);

    const handleCreated = (newRes: Reservation) => {
      // Only care if it belongs to this board
      if (newRes.boardId?.toString() === boardId.toString() || !newRes.boardId) {
        setReservations(prev => {
          if (prev.some(r => r.id === newRes.id)) return prev;
          return [...prev, newRes].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
        });
      }
    };

    const handleUpdated = (updatedRes: Reservation) => {
      setReservations(prev => prev.map(r => r.id === updatedRes.id ? updatedRes : r));
    };

    const handleDeleted = ({ id }: { id: string }) => {
      setReservations(prev => prev.filter(r => r.id !== id));
    };

    const handleRestored = (restoredRes: Reservation) => {
       if (restoredRes.boardId?.toString() === boardId.toString() || !restoredRes.boardId) {
          setReservations(prev => [...prev, restoredRes].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()));
          setDeletedReservations(prev => prev.filter(r => r.id !== restoredRes.id));
       }
    };

    socket.on('reservationCreated', handleCreated);
    socket.on('reservationUpdated', handleUpdated);
    socket.on('reservationDeleted', handleDeleted);
    socket.on('reservationRestored', handleRestored);

    return () => {
      socket.off('reservationCreated', handleCreated);
      socket.off('reservationUpdated', handleUpdated);
      socket.off('reservationDeleted', handleDeleted);
      socket.off('reservationRestored', handleRestored);
    };
  }, [socket, boardId]);

  return {
    board,
    reservations,
    deletedReservations,
    loading,
    error,
    refetchBoard: fetchBoard,
    refetchReservations: fetchReservations,
    refetchAll: fetchAllData,
    updateBoard,
    updateReservation,
    deleteReservation,
    restoreReservation,
    setError
  };
};